import React, { useState, useMemo, useEffect, useRef, ReactNode } from 'react';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import { SideMenuBoxRecoil } from '@/atom/ModalAtom';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import LS from '@/styles/layoutStyled';
import CS from '@/styles/componentStyled';
import BS from '@/styles/btnStyled';
import IS from '@/styles/inputStyled';

interface MenuItem {
  title: string;
  icoUrl?: string;
  icoUrlActive?: string;
  url?: string;
  subMenu?: MenuItem[];
}

const menuItems: MenuItem[] = [
  {
    title: '대시보드',
    icoUrl: './img/side_menu_ico_dashboard.svg',
    icoUrlActive: './img/side_menu_ico_dashboard_active.svg',
    url: '/dashboard',
  },
  {
    title: '출입 관리',
    icoUrl: './img/side_menu_ico_coming.svg',
    icoUrlActive: './img/side_menu_ico_coming_active.svg',
    url: '/entrymanagement',
  },
  // {
  //   title: '근태 관리',
  //   icoUrl: './img/side_menu_ico_attendance.svg',
  //   icoUrlActive: './img/side_menu_ico_attendance_active.svg',
  //   url: '/dashboard',
  // },
  {
    title: '운영',
    icoUrl: './img/side_menu_ico_operate.svg',
    icoUrlActive: './img/side_menu_ico_operate_active.svg',
    subMenu: [
      { title: '출입장소 관리', url: '/placemanagement' },
      { title: '출입신분증 관리', url: '/idcardmanagement' },
      // { title: '계정 관리', url: '/dashboard' },
    ],
  },
];

const SideMenuBox = () => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [activeSubIndex, setActiveSubIndex] = useState<number | null>(null);
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);

  const [sideMenuBoxState, setSideMenuBoxState] =
    useRecoilState(SideMenuBoxRecoil);

  useEffect(() => {
    const storedActiveIndex = sessionStorage.getItem('activeIndex');
    const storedActiveSubIndex = sessionStorage.getItem('activeSubIndex');
    const storedOpenMenuIndex = sessionStorage.getItem('openMenuIndex');

    if (storedActiveIndex) setActiveIndex(Number(storedActiveIndex));
    if (storedActiveSubIndex) setActiveSubIndex(Number(storedActiveSubIndex));
    if (storedOpenMenuIndex) setOpenMenuIndex(Number(storedOpenMenuIndex));
  }, []);

  const handleClick = (index: number, hasSubMenu?: boolean) => {
    setActiveIndex(index);
    setOpenMenuIndex(
      hasSubMenu ? (openMenuIndex === index ? null : index) : null,
    );
    setActiveSubIndex(null);
    sessionStorage.setItem('activeIndex', index.toString());
    if (hasSubMenu) {
      sessionStorage.setItem('openMenuIndex', index.toString());
    } else {
      sessionStorage.removeItem('openMenuIndex');
    }
    sessionStorage.removeItem('activeSubIndex');
  };

  const handleSubClick = (index: number, subIndex: number) => {
    setActiveIndex(index);
    setActiveSubIndex(subIndex);
    sessionStorage.setItem('activeIndex', index.toString());
    sessionStorage.setItem('activeSubIndex', subIndex.toString());
  };

  return (
    <Wrap className={sideMenuBoxState ? 'active' : ''}>
      <button
        type="button"
        className="x-btn img-box"
        onClick={() => setSideMenuBoxState(false)}
      >
        <img src="./img/ico_x_444.svg" alt="" />
      </button>
      <span className="logo-box img-box mgc">
        <img src="./img/logo.svg" alt="" />
      </span>
      <MenuBox>
        {menuItems.map((item, index) => (
          <li key={index}>
            <Link
              to={item.url || '#'}
              className={activeIndex === index ? 'active rel' : 'rel'}
              onClick={() => handleClick(index, !!item.subMenu)}
            >
              <div className="inner-box flex aic">
                <span className="img-box">
                  <img
                    src={
                      activeIndex !== index ? item.icoUrl : item.icoUrlActive
                    }
                    alt=""
                  />
                </span>
                <span className="text tm">{item.title}</span>
              </div>
            </Link>
            {item.subMenu && openMenuIndex === index && (
              <ul className="sub-menu">
                {item.subMenu.map((subItem, subIndex) => (
                  <li key={subIndex}>
                    <Link
                      to={subItem.url || '#'}
                      className={
                        activeSubIndex === subIndex ? 'active rel' : 'rel'
                      }
                      onClick={() => handleSubClick(index, subIndex)}
                    >
                      {subItem.title}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </MenuBox>
    </Wrap>
  );
};

export default SideMenuBox;

const Wrap = styled.div`
  background: #fff;
  width: 240px;
  min-height: 100vh;
  height: 100%;
  box-sizing: border-box;
  border-right: 1px solid #eeeeff;
  padding: 24px 0 0 0;
  > .x-btn {
    display: none;
  }
  > .logo-box {
    width: 129px;
  }
  @media (max-width: 1420px) {
    border-right: none;
    box-shadow: 4px 4px 20px 0 rgba(58, 58, 58, 0.44);
    position: absolute;
    z-index: 2;
    top: 0;
    right: 0;
    display: none;
    padding: 48px 0 0 0;
    transition: all 0.3s;
    &.active {
      display: block;
    }
    > .x-btn {
      width: 20px;
      display: block;
      position: absolute;
      top: 20px;
      right: 20px;
    }
    > .logo-box {
      display: none;
    }
  }
`;

const MenuBox = styled.ul`
  width: 100%;
  margin-top: 30px;
  > li {
    width: 100%;
    > a {
      width: 100%;
      box-sizing: border-box;
      padding: 0 24px;
      &::after {
        content: '';
        display: none;
        width: 4px;
        height: 100%;
        background: ${(props) => props.theme.mainColor1};
        position: absolute;
        top: 0;
        left: 0;
        border-radius: 0 4px 4px 0;
      }
      > .inner-box {
        width: 100%;
        box-sizing: border-box;
        padding: 14px 16px;
        border-radius: 6px;
        gap: 16px;
        > .img-box {
          width: 22px;
        }
        > .text {
          font-size: 16px;
          color: #202224;
        }
      }
    }
    > a.active {
      &::after {
        display: block;
      }
      > .inner-box {
        background: ${(props) => props.theme.mainColor1};
        > .text {
          font-weight: 700;
          color: #fff;
        }
      }
    }
    .sub-menu {
      box-sizing: border-box;
      padding: 9px 0;
      > li {
        > a {
          box-sizing: border-box;
          padding: 9px 9px 9px 78px;
          font-size: 16px;
          color: #444;
          &.active {
            color: ${(props) => props.theme.mainColor1};
          }
          &::after {
            content: '';
            display: block;
            width: 4px;
            height: 4px;
            background: ${(props) => props.theme.mainColor1};
            border-radius: 50%;
            position: absolute;
            top: 50%;
            left: 58px;
            transform: translateY(-50%);
          }
        }
      }
    }
  }
`;
